import { push, update, child } from 'firebase/database';
import { visitSessionsRef } from '../config/firebase';
import { getDeviceInfo } from './deviceInfo';

// Plain Firebase write, not a redux action - matches how Login/CustomNavbar
// already talk to Supabase directly for auth. Keeping this out of the
// shared `data` reducer on purpose: that reducer is a single bucket that
// gets fully overwritten by whichever fetch (messages, game scores, ...)
// resolves last, so a high-frequency writer like page visits has no
// business going anywhere near it.

// Storage shape: one doc per browser session under visitSessions/{sessionId}
// holding the heavy stuff (geo + device) ONCE, with every page view nested
// inside as a tiny { path, timestamp }. Previously each page nav wrote a
// brand new top-level entry with the *entire* geo blob (ip, city, region,
// country, isp, timezone, connection, flag, ...) copied into it again -
// visiting 5 pages meant that whole payload saved 5 times over. Now it's
// saved once per visit, and every extra page is just a couple bytes.
const SESSION_ID_KEY = 'visitorSessionId';
const SESSION_LOGGED_KEY = 'visitorSessionLogged';

const getOrCreateSessionId = () => {
    try {
        let id = sessionStorage.getItem(SESSION_ID_KEY);
        if (!id) {
            id = (window.crypto && window.crypto.randomUUID)
                ? window.crypto.randomUUID()
                : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            sessionStorage.setItem(SESSION_ID_KEY, id);
        }
        return id;
    } catch (error) {
        // sessionStorage unavailable (private mode, quota) - falls back to
        // a fresh id per call, so this one visitor just won't get grouped
        // across their own page views. Never throws either way.
        return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
};

// Free, no-signup, HTTPS, CORS-friendly IP geolocation lookup. Runs from
// the visitor's own browser (this site has no backend to proxy it through).
const GEO_LOOKUP_URL = 'https://ipwho.is/';

// ipwho.is's free tier is capped at 1000 requests/day, and CORS requests
// are counted per calling domain, not per visitor IP - so that whole
// budget is shared across every visitor hitting this site on a given day.
// Only ever called once per session (see isNewSession below), same as the
// session doc itself.
const fetchGeo = async () => {
    try {
        const response = await fetch(GEO_LOOKUP_URL);
        const data = await response.json();
        if (!data || data.success === false) return null;
        return data;
    } catch (error) {
        console.error('Error looking up visitor location:', error);
        return null;
    }
};

export const trackVisit = async (path) => {
    // Every visit is saved (never skipped for admins either) - tagged
    // isAdmin so Analytics can filter its own traffic back out instead of
    // it vanishing with no trace in the Firebase console.
    const isAdmin = !!localStorage.getItem('adminLoggedIn');

    const sessionId = getOrCreateSessionId();
    const now = Date.now();

    let isNewSession = false;
    try {
        isNewSession = !sessionStorage.getItem(SESSION_LOGGED_KEY);
    } catch (error) {
        isNewSession = true;
    }

    // Pre-generate the pageView's push id without writing yet (push() on a
    // ref with no value just reserves a key locally).
    const pageViewId = push(child(visitSessionsRef, `${sessionId}/pageViews`)).key;

    // Everything below goes out as ONE atomic multi-path update instead of
    // separate set()/push() calls. Reason: the old code called
    // sessionStorage.setItem(SESSION_LOGGED_KEY, '1') right after *firing*
    // the geo/device set() - not after it actually finished - so if that
    // first write ever failed (cold page load, connection still opening,
    // any transient hiccup), the tab was permanently marked "already
    // logged" and every later page view that session only ever touched
    // lastSeen/pageViews, never retrying geo/device. That's exactly the
    // "pageViews there, geo/device missing" shape seen in the database.
    // A single update() either saves everything for this call or nothing,
    // and the flag below is only set once it's confirmed to have landed.
    const updates = {};
    let geo = null;
    if (isNewSession) {
        geo = await fetchGeo();
        updates[`${sessionId}/geo`] = geo || null;
        updates[`${sessionId}/device`] = getDeviceInfo();
        updates[`${sessionId}/referrer`] = document.referrer || 'direct';
        updates[`${sessionId}/firstSeen`] = now;
        updates[`${sessionId}/isAdmin`] = isAdmin;
    }
    updates[`${sessionId}/lastSeen`] = now;
    updates[`${sessionId}/pageViews/${pageViewId}`] = { path, timestamp: now };

    update(visitSessionsRef, updates)
        .then(() => {
            if (isNewSession) {
                try { sessionStorage.setItem(SESSION_LOGGED_KEY, '1'); } catch (error) { /* ignore */ }
            }
        })
        .catch((error) => {
            // Never let a logging failure be visible to a real visitor -
            // and since the flag above only gets set on success, a failed
            // new-session write will simply retry in full on the next
            // page view instead of getting stuck half-saved.
            console.error('Error logging visit:', error);
        });
};
